import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  MessageSquare,
  FileText,
  Users,
  EyeOff,
  Bot as BotIcon,
  Settings as SettingsIcon,
  LogOut,
  MessagesSquare,
  KeyRound,
} from "lucide-react";

import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, adminOnly: true, testid: "nav-dashboard" },
  { to: "/conversations", label: "Conversations", icon: MessagesSquare, testid: "nav-conversations" },
  { to: "/templates", label: "Templates", icon: FileText, adminOnly: true, testid: "nav-templates" },
  { to: "/staff", label: "Staff", icon: Users, adminOnly: true, testid: "nav-staff" },
  { to: "/private-numbers", label: "Private Numbers", icon: EyeOff, adminOnly: true, testid: "nav-private-numbers" },
  { to: "/bot", label: "Auto-responder", icon: BotIcon, adminOnly: true, testid: "nav-bot" },
  { to: "/keys", label: "API Keys", icon: KeyRound, adminOnly: true, testid: "nav-api-keys" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, adminOnly: true, testid: "nav-settings" },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="grid h-screen grid-cols-[240px_1fr] overflow-hidden bg-background text-foreground">
      <aside className="flex h-full flex-col border-r border-border bg-background">
        <div className="flex items-center gap-2.5 border-b border-border px-5 py-5">
          <div className="flex h-9 w-9 items-center justify-center bg-primary text-primary-foreground">
            <MessageSquare className="h-4 w-4" aria-hidden="true" />
          </div>
          <div className="leading-tight">
            <div className="font-display text-sm font-bold">WABA CRM</div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Staff Console</div>
          </div>
        </div>

        <nav className="flex-1 space-y-0.5 p-3">
          {nav
            .filter((n) => !n.adminOnly || user?.role === "admin")
            .map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 border border-transparent px-3 py-2.5 text-sm transition-colors duration-150 ${
                    isActive
                      ? "border-border bg-secondary text-foreground"
                      : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                  }`
                }
              >
                <item.icon className="h-4 w-4" aria-hidden="true" />
                <span className="font-medium">{item.label}</span>
              </NavLink>
            ))}
        </nav>

        <div className="border-t border-border p-3">
          <div className="mb-3 border border-border p-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">Signed in</div>
            <div className="mt-1 truncate font-display text-sm font-bold" data-testid="current-user-name">
              {user?.name || "—"}
            </div>
            <div className="mt-0.5 font-mono text-xs text-muted-foreground">+91 {user?.mobile}</div>
            <div className="mt-2 inline-flex items-center border border-border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest">
              {user?.role}
            </div>
          </div>
          <Button
            variant="outline"
            onClick={onLogout}
            className="w-full justify-start gap-2 rounded-sm"
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" />
            Sign out
          </Button>
        </div>
      </aside>

      <main className="h-full overflow-y-auto">
        <Outlet />
      </main>
    </div>
  );
}
